// frontend/src/components/notifications/NotificationPanel.jsx
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './NotificationPanel.css';

// ─── Métadonnées visuelles par type ──────────────────────────────────────────
const TYPE_META = {
  partner_pending:     { icon: 'fa-user-clock',          color: '#f59e0b', cat: 'partner'  },
  partner_approved:    { icon: 'fa-check-circle',         color: '#10b981', cat: 'partner'  },
  partner_rejected:    { icon: 'fa-times-circle',         color: '#ef4444', cat: 'partner'  },
  partner_suspended:   { icon: 'fa-pause-circle',         color: '#f97316', cat: 'partner'  },
  partner_reactivated: { icon: 'fa-play-circle',          color: '#10b981', cat: 'partner'  },
  partner_purchase:    { icon: 'fa-shopping-cart',        color: '#3b82f6', cat: 'payment'  },
  client_registered:   { icon: 'fa-user-plus',            color: '#3b82f6', cat: 'client'   },
  account_suspended:   { icon: 'fa-ban',                  color: '#ef4444', cat: 'client'   },
  account_reactivated: { icon: 'fa-user-check',           color: '#10b981', cat: 'client'   },
  login_failed:              { icon: 'fa-exclamation-triangle', color: '#ef4444', cat: 'security' },
  client_login_failed:       { icon: 'fa-user-times',           color: '#ef4444', cat: 'security' },
  webhook_failed:            { icon: 'fa-exclamation-circle',   color: '#ef4444', cat: 'security' },
  payment_success:           { icon: 'fa-coins',                color: '#10b981', cat: 'payment'  },
  payment_success_client:    { icon: 'fa-coins',                color: '#10b981', cat: 'payment'  },
  payment_failed:            { icon: 'fa-times-circle',         color: '#ef4444', cat: 'payment'  },
  stock_critical:            { icon: 'fa-battery-quarter',      color: '#f97316', cat: 'admin'    },
  partner_api_key_created:   { icon: 'fa-key',                  color: '#3b82f6', cat: 'security' },
  partner_api_key_revoked:   { icon: 'fa-key',                  color: '#f97316', cat: 'security' },
  admin_created:             { icon: 'fa-user-shield',          color: '#8b5cf6', cat: 'admin'    },
  admin_suspended:           { icon: 'fa-user-lock',            color: '#ef4444', cat: 'admin'    },
  admin_security_changed:    { icon: 'fa-shield-alt',           color: '#f59e0b', cat: 'security' },
};
const FALLBACK = { icon: 'fa-bell', color: '#3b82f6', cat: 'admin' };
const getMeta = (type) => TYPE_META[type] || FALLBACK;

// ─── Filtres ──────────────────────────────────────────────────────────────────
const FILTERS = [
  { key: 'all',      label: 'Tous'        },
  { key: 'partner',  label: 'Partenaires' },
  { key: 'client',   label: 'Clients'     },
  { key: 'payment',  label: 'Paiement'    },
  { key: 'security', label: 'Sécurité'    },
  { key: 'admin',    label: 'Admin'       },
];

// ─── Temps relatif ────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60)  return "à l'instant";
  const mins = Math.floor(secs / 60);
  if (mins < 60)  return `il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'hier';
  if (days < 7)   return `il y a ${days}j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

// ─── Animations ───────────────────────────────────────────────────────────────
const panelVariants = {
  hidden: { x: '100%', opacity: 0.6 },
  visible: {
    x: 0, opacity: 1,
    transition: { type: 'spring', stiffness: 280, damping: 30 }
  },
  exit: {
    x: '100%', opacity: 0.6,
    transition: { type: 'spring', stiffness: 320, damping: 32 }
  },
};

const backdropVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1 },
  exit:    { opacity: 0 },
};

const itemVariants = {
  hidden:  { opacity: 0, x: -12 },
  visible: (i) => ({ opacity: 1, x: 0, transition: { delay: i * 0.04, duration: 0.22 } }),
};

// ─── Composant ────────────────────────────────────────────────────────────────
const NotificationPanel = ({
  open,
  onClose,
  notifications = [],
  unreadCount = 0,
  onMarkRead,
  onMarkAllRead,
  onRemove,
  onNotifClick,
}) => {
  const [tab, setTab]           = useState('all');     // 'unread' | 'all'
  const [filter, setFilter]     = useState('all');

  const filtered = useMemo(() => {
    let list = notifications;
    if (tab === 'unread') list = list.filter(n => !n.is_read);
    if (filter !== 'all') list = list.filter(n => getMeta(n.type).cat === filter);
    return list;
  }, [notifications, tab, filter]);

  const handleItemClick = (notif) => {
    if (!notif.is_read) onMarkRead(notif.id);
    onClose();
    onNotifClick(notif);
  };

  const handleRemove = (e, id) => {
    e.stopPropagation();
    onRemove(id);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="np-backdrop"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className="np-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            role="dialog"
            aria-label="Panneau de notifications"
          >
            {/* Header */}
            <div className="np-header">
              <div className="np-header-left">
                <i className="far fa-bell np-header-icon"></i>
                <h2 className="np-title">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="np-unread-pill">{unreadCount} non lues</span>
                )}
              </div>
              <button className="np-close-btn" onClick={onClose} aria-label="Fermer">
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Onglets */}
            <div className="np-tabs">
              <button
                className={`np-tab${tab === 'all' ? ' active' : ''}`}
                onClick={() => setTab('all')}
              >
                Toutes
                <span className="np-tab-count">{notifications.length}</span>
              </button>
              <button
                className={`np-tab${tab === 'unread' ? ' active' : ''}`}
                onClick={() => setTab('unread')}
              >
                Non lues
                {unreadCount > 0 && (
                  <span className="np-tab-count unread">{unreadCount}</span>
                )}
              </button>
            </div>

            {/* Filtres */}
            <div className="np-filters">
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  className={`np-filter-chip${filter === f.key ? ' active' : ''}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Actions globales */}
            {unreadCount > 0 && (
              <div className="np-actions-bar">
                <button className="np-mark-all-btn" onClick={onMarkAllRead}>
                  <i className="fas fa-check-double"></i>
                  Tout marquer comme lu
                </button>
              </div>
            )}

            {/* Liste */}
            <div className="np-list">
              {filtered.length === 0 ? (
                <div className="np-empty">
                  <i className="far fa-bell-slash np-empty-icon"></i>
                  <p>Aucune notification</p>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {filtered.map((notif, i) => {
                    const meta = getMeta(notif.type);
                    return (
                      <motion.div
                        key={notif.id}
                        className={`np-item${!notif.is_read ? ' unread' : ''}`}
                        custom={i}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit={{ opacity: 0, x: -20, transition: { duration: 0.18 } }}
                        onClick={() => handleItemClick(notif)}
                        layout
                      >
                        {/* Icône */}
                        <div
                          className="np-item-icon"
                          style={{ '--np-color': meta.color }}
                        >
                          <i className={`fas ${meta.icon}`}></i>
                        </div>

                        {/* Corps */}
                        <div className="np-item-body">
                          <div className="np-item-top">
                            <span className="np-item-title">{notif.title}</span>
                            <span className="np-item-time">{timeAgo(notif.created_at)}</span>
                          </div>
                          <p className="np-item-msg">{notif.message}</p>
                          {notif.target_url && (
                            <span className="np-item-action">
                              <i className="fas fa-arrow-right"></i>
                              Voir l'action
                            </span>
                          )}
                        </div>

                        {/* Dot non-lu */}
                        {!notif.is_read && <span className="np-item-dot" />}

                        {/* Supprimer */}
                        <button
                          className="np-item-delete"
                          onClick={(e) => handleRemove(e, notif.id)}
                          aria-label="Supprimer"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationPanel;
